import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  InstitutionSubjectsInfo,
  useGetQuestionsGroupedBySubject,
  useListMainQuestionStylesFrom,
} from '../api/exam-analysis';

import {
  MultiSelectOption,
  SelectOption,
} from '../typings/select-option.typings';

interface Interface {
  form: {
    uf: string;
    institution: string;
    area: MultiSelectOption;
  };
  depth: number;
  list: InstitutionSubjectsInfo[];
  open: string;
  questionOpen: string;
  showAnswer: boolean;
  styles: string[];
  loading: boolean;
  actions: {
    onArea: (a: SelectOption) => () => void;
    onUf: (value: string) => void;
    onInstitution: (value: string) => void;
    onDepth: (d: 1 | 2 | 3 | 4) => () => void;
    onOpen: (s: string) => () => void;
    onQuestionOpen: (s: string) => () => void;
    onShowAnswer: () => void;
  };
}

const ExamAnalysisContext = createContext({} as Interface);

export function useExamAnalysisContext(): Interface {
  return useContext(ExamAnalysisContext);
}

const ExamAnalysisProvider: FC<{
  institutionName?: string; ufName?: string;
}> = ({ children, institutionName, ufName }) => {
  const [depth, setDepth] = useState(2);
  const [open, setOpen] = useState('');
  const [questionOpen, setQuestionOpen] = useState('');
  const [uf, setUf] = useState(ufName ?? '');
  const [institution, setInstitution] = useState(institutionName ?? '');
  const [area, setArea] = useState<MultiSelectOption>([]);
  const [showAnswer, setShowAnswer] = useState(false);

  /** Queries */

  const query = useGetQuestionsGroupedBySubject({
    institutionName: institution,
    subjectDepth: depth,
  });

  const styleQuery = useListMainQuestionStylesFrom({
    institutionName: institution,
  });

  /** Memos */
  const areas = useMemo(() => {
    return area.map((i) => i.value);
  }, [area]);

  const styles = useMemo(() => {
    return styleQuery.data?.styles.map((i) => i.style) || [];
  }, [styleQuery.data]);

  const list = useMemo(() => {
    if (query.data) {
      const info = query.data.subjectsInfo;
      return [...info]
        .filter((i) => {
          if (areas.length === 0) {
            return true;
          }
          return areas.includes(i.ancestors[0]);
        })
        .sort((a, b) => {
          return a.questionCount > b.questionCount ? -1 : 1;
        });
    }
    return [];
  }, [areas, query.data]);

  /** Callbacks */

  const onDepth = useCallback(
    (d: 1 | 2 | 3 | 4) => (): void => {
      setDepth(d);
    },
    [],
  );

  const onOpen = useCallback(
    (s: string) => (): void => {
      setOpen((old) => {
        if (old === s) {
          return '';
        }
        return s;
      });
    },
    [],
  );

  const onQuestionOpen = useCallback(
    (s: string) => (): void => {
      setQuestionOpen((old) => {
        if (old === s) {
          return '';
        }
        return s;
      });
    },
    [],
  );

  const onShowAnswer = useCallback(() => {
    setShowAnswer((old) => !old);
  }, []);

  const onUf = useCallback((value: string) => {
    console.log(value);
    setInstitution('');
    setUf(value);
  }, []);

  const onInstitution = useCallback((value: string) => {
    console.log(value);
    setInstitution(value);
  }, []);

  const onArea = useCallback(
    (value: SelectOption) => (): void => {
      const find = area.find((i) => i.value === value.value);
      if (find) {
        setArea(area.filter((i) => i.value !== value.value));
      } else {
        setArea([...area, value]);
      }
    },
    [area],
  );

  return (
    <ExamAnalysisContext.Provider
      value={{
        form: {
          area,
          institution,
          uf,
        },
        depth,
        list,
        loading: query.isLoading,
        open,
        questionOpen,
        showAnswer,
        styles,
        actions: {
          onArea,
          onInstitution,
          onUf,
          onDepth,
          onOpen,
          onQuestionOpen,
          onShowAnswer,
        },
      }}
    >
      {children}
    </ExamAnalysisContext.Provider>
  );
};

export default ExamAnalysisProvider;
